import { HttpClient, HttpParams } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import {
  FinlexActDetail,
  FinlexActsResponse,
  FinlexDatasetsResponse,
  FinlexDocumentType,
  FinlexLanguage,
  FinlexSearchParams,
} from './finlex.models';

@Injectable({
  providedIn: 'root',
})
export class FinlexApiService {
  private readonly http = inject(HttpClient);
  private readonly baseUrl = '/api/v1/finlex';

  getDatasets(): Observable<FinlexDatasetsResponse> {
    return this.http.get<FinlexDatasetsResponse>(`${this.baseUrl}/datasets`);
  }

  searchActs(params: FinlexSearchParams): Observable<FinlexActsResponse> {
    return this.http.get<FinlexActsResponse>(`${this.baseUrl}/acts`, {
      params: this.searchParams(params),
    });
  }

  getAct(
    documentType: FinlexDocumentType,
    year: string,
    number: string,
    language: FinlexLanguage,
    langAndVersion?: string,
  ): Observable<FinlexActDetail> {
    let params = new HttpParams().set('language', language);
    if (langAndVersion !== undefined) params = params.set('langAndVersion', langAndVersion);
    return this.http.get<FinlexActDetail>(
      `${this.baseUrl}/acts/${documentType}/${year}/${number}`,
      {
        params,
      },
    );
  }

  getActXml(
    documentType: FinlexDocumentType,
    year: string,
    number: string,
    langAndVersion: string,
  ): Observable<string> {
    return this.http.get(`${this.baseUrl}/acts/${documentType}/${year}/${number}/xml`, {
      params: new HttpParams().set('langAndVersion', langAndVersion),
      responseType: 'text',
    });
  }

  private searchParams(params: FinlexSearchParams): HttpParams {
    const query = this.optionalString(params.query);
    const startYear = this.optionalString(params.startYear);
    const endYear = this.optionalString(params.endYear);
    let httpParams = new HttpParams()
      .set('documentType', params.documentType)
      .set('language', params.language)
      .set('limit', params.limit)
      .set('offset', params.offset);

    if (query) {
      httpParams = httpParams.set('query', query);
    }
    if (startYear) {
      httpParams = httpParams.set('startYear', startYear);
    }
    if (endYear) {
      httpParams = httpParams.set('endYear', endYear);
    }

    return httpParams;
  }

  private optionalString(value: string | number | null): string {
    return value === null ? '' : String(value).trim();
  }
}
